import Box from '@mui/material/Box';
import { ACL_TYPE } from '@openx/types/aclType';
import type { PermissionsCheck } from '@openx/types/permissionsCheck';
import { useIsAllowed } from '@openx/utils/permission/useIsAllowed';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { OpenXAgentDealPromptView } from '../components/DealPrompt/OpenXAgentDealPromptView';
import { OpenXAgentTriggerFAB } from '../components/FAB/OpenXAgentTriggerFAB';
import { OpenXAgentHomeView } from '../components/Home/OpenXAgentHomeView';
import { OPENX_AGENT_TOOL } from '../components/Home/utils/consts';
import type { OpenXAgentTool } from '../components/Home/utils/types';
import { OpenXAgentModal } from '../components/Modal/OpenXAgentModal';
import { OpenXAgentDesignSystemTheme } from '../components/Theme/OpenXAgentDesignSystemTheme';
import { TargetingExplainer } from '../pages/TargetingExplainer/TargetingExplainer';
import { OpenXAgentContext, type OpenXAgentSidePanel, type OpenXAgentToolType } from './OpenXAgentContext';
import { readStoredOpen, writeStoredOpen } from './openXAgentStorage';

type OpenXAgentContextProviderProps = {
  children: ReactNode;
  isAllowed?: (check: PermissionsCheck) => boolean;
  userName?: string;
};

export const OpenXAgentContextProvider = ({
  children,
  isAllowed: isAllowedOverride,
  userName = '',
}: OpenXAgentContextProviderProps) => {
  if (isAllowedOverride) {
    return (
      <OpenXAgentContent isAllowed={isAllowedOverride} userName={userName}>
        {children}
      </OpenXAgentContent>
    );
  }

  return (
    <OpenXAgentContentWithSharedPermissions userName={userName}>{children}</OpenXAgentContentWithSharedPermissions>
  );
};

const OpenXAgentContentWithSharedPermissions = ({ children, userName }: { children: ReactNode; userName: string }) => {
  const { isAllowed } = useIsAllowed();

  return (
    <OpenXAgentContent isAllowed={isAllowed} userName={userName}>
      {children}
    </OpenXAgentContent>
  );
};

const OpenXAgentContent = ({
  children,
  isAllowed,
  userName,
}: {
  children: ReactNode;
  isAllowed: (check: PermissionsCheck) => boolean;
  userName: string;
}) => {
  const location = useLocation();
  const isTroubleshootingView = location.pathname.includes('/troubleshoot');

  const [isOpen, setIsOpenState] = useState(() => readStoredOpen());
  const [sidePanel, setSidePanel] = useState<OpenXAgentSidePanel | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeTool, setActiveTool] = useState<OpenXAgentToolType>({
    toolId: null,
    toolData: null,
  });

  const setIsOpen = useCallback((next: boolean) => {
    setIsOpenState(next);
    writeStoredOpen(next);
    if (!next) setIsMenuOpen(false);
  }, []);

  const openSidePanel = useCallback((content: OpenXAgentSidePanel) => {
    setSidePanel(content);
  }, []);

  const closeSidePanel = useCallback(() => {
    setSidePanel(null);
  }, []);

  // side panels belong to the tool that opened them, so selecting or leaving a tool closes the current one
  const setActiveToolId = useCallback((toolId: OpenXAgentTool | null) => {
    setActiveTool({ toolId, toolData: null });
    setSidePanel(null);
  }, []);

  const changeActiveTool = useCallback((nextTool: OpenXAgentToolType) => {
    setActiveTool(nextTool);
    setSidePanel(null);
  }, []);

  const contextValue = useMemo(
    () => ({
      isOpenXAgentOpen: isOpen,
      setIsOpenXAgentOpen: setIsOpen,
      sidePanel,
      openSidePanel,
      closeSidePanel,
      isMenuOpen,
      setIsMenuOpen,
      activeTool,
      setActiveTool: changeActiveTool,
      setActiveToolId,
    }),
    [
      isOpen,
      setIsOpen,
      sidePanel,
      openSidePanel,
      closeSidePanel,
      isMenuOpen,
      activeTool,
      changeActiveTool,
      setActiveToolId,
    ]
  );

  const renderActiveView = () => {
    if (activeTool.toolId === null) return <OpenXAgentHomeView userName={userName} onSelectTool={setActiveToolId} />;

    // only the Deal Prompt and Targeting Explainer agents have a screen so far; other agents render an empty body
    if (activeTool.toolId === OPENX_AGENT_TOOL.DEAL_PROMPT) return <OpenXAgentDealPromptView />;
    if (activeTool.toolId === OPENX_AGENT_TOOL.TARGETING_EXPLAINER) return <TargetingExplainer userName={userName} />;

    return null;
  };

  const isOpenXAgentAllowed = isAllowed(isAllowedByCompiledAcl(ACL_TYPE.XPERT_ENABLED));
  const isOpenXAgentEnabled = isOpenXAgentAllowed && !isTroubleshootingView;

  if (!isOpenXAgentEnabled) return children;

  return (
    <OpenXAgentContext.Provider value={contextValue}>
      <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
        {children}
        <OpenXAgentDesignSystemTheme>
          <OpenXAgentTriggerFAB />
          {isOpen && <OpenXAgentModal>{renderActiveView()}</OpenXAgentModal>}
        </OpenXAgentDesignSystemTheme>
      </Box>
    </OpenXAgentContext.Provider>
  );
};
